import { useEffect } from 'react'

import { Colors } from 'src/resources/colors'
import EmbedCheckIcon from 'src/resources/icons/EmbedCheck.svg'
import EmbedLockIcon from 'src/resources/icons/EmbedLock.svg'
import styled, { css } from 'styled-components'

import { IUploadDataSource } from '../types/IUploadDataSource'
import { themed } from 'src/applications/Embed/fragments/ThemeWrapper'
import { useTranslation } from 'react-i18next'

const SheetsTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead > tr > th {
    border-bottom: 1px solid ${themed('borderColor')};
  }

  th,
  td {
    font-size: ${themed('contentFontSize')};
    font-weight: 500;
    line-height: 24px;
    text-align: left;
    color: ${themed('textColor')};
    padding: 24px;
  }

  th {
    padding-top: 0;
  }
`

const Checkbox = styled.div`
  width: 24px;
  height: 24px;
  border: 1px solid ${themed('borderColor')};
  box-sizing: border-box;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  border-radius: 50%;
`
const Row = styled.tr<{ locked?: boolean; selected?: boolean }>`
  ${({ selected }) =>
    selected &&
    css`
      ${Checkbox} {
        border-color: ${(props) => props.theme.primary || Colors.brandPrimary};

        &:after {
          content: '';
          display: block;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          background-color: ${(props) => props.theme.primary || Colors.brandPrimary};
          position: relative;
          top: 1px;
          left: 1px;
        }
      }
    `}

  ${({ locked }) =>
    locked
      ? css`
          ${Checkbox} {
            background-color: #99acc2;
            display: flex;
            justify-content: center;
            align-items: center;

            &:after {
              content: '';
              display: block;
              width: 100%;
              height: 100%;
              background-image: url(${EmbedLockIcon});
              background-repeat: no-repeat;
              background-position: center;
            }
          }
        `
      : css`
          th,
          td {
            color: #33475b;
          }

          &:hover {
            cursor: pointer;
            background-color: #f4f8fa;
            transition: background 0.3s;
          }
        `}
`

Row.defaultProps = {
  theme: {
    primary: Colors.brandPrimary
  }
}

interface IDataSourceTable {
  autoSelectSingleOption?: boolean
  uploadDataSources: IUploadDataSource[]
  selected?: number
  onChange(s?: number): void
}
export function DataSourceTable({
  autoSelectSingleOption = true,
  uploadDataSources,
  selected,
  onChange
}: IDataSourceTable) {
  const { t } = useTranslation()
  useEffect(() => {
    if (autoSelectSingleOption) {
      if (uploadDataSources.length === 1) {
        if (typeof selected !== 'number') {
          onChange(uploadDataSources[0].index)
        }
      }
    }
  }, [autoSelectSingleOption, uploadDataSources, selected, onChange])
  return (
    <SheetsTable>
      <colgroup>
        <col style={{ width: '10%' }} />
        <col style={{ width: '80%' }} />
        <col style={{ width: '10%' }} />
      </colgroup>
      <thead>
        <tr>
          <th />
          <th>{t('components.DataSourceTable.sheetTitle')}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {uploadDataSources.map(({ index, label }) => {
          const checked = index === selected
          return (
            <Row
              key={index}
              onClick={() => onChange(index)}
              selected={checked}
              locked={false} // Temporary disabled (FLA-1785)
            >
              <td>
                <Checkbox data-testid={`source-index-${index}-checkbox`} aria-checked={checked} />
              </td>
              <td>{label}</td>
              <td>{checked && <img src={EmbedCheckIcon} />}</td>
            </Row>
          )
        })}
      </tbody>
    </SheetsTable>
  )
}
